import { ChevronDown, ChevronUp } from "@tamagui/lucide-icons";
import {
  Children,
  Fragment,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useMatch, useNavigate } from "react-router";
import {
  YStack,
  Button,
  ButtonProps,
  Nav,
  ButtonContext,
  XStack,
  withStaticProperties,
  Spinner,
} from "tamagui";
import { GradientButton, getThemedIcon } from "tamagui-config";

const NavigationComponent = ({ children }: { children: ReactNode }) => (
  <Nav gap="$3.5">{children}</Nav>
);

type NavigationButtonProps = ButtonProps & {
  routePath?: string;
  isActive?: boolean;
  isLoading?: boolean;
  isDropdownMenuTrigger?: boolean;
};

const NavigationButton = ({
  icon,
  iconAfter,
  routePath,
  isActive: isActiveProp,
  isLoading,
  isDropdownMenuTrigger,
  onPress: onPressProp,
  children,
  ...rest
}: NavigationButtonProps) => {
  const navigate = useNavigate();
  const { color } = useContext(ButtonContext);
  const dropdownMenuContext = useContext(NavigationDropdownMenuContext);

  const routeMatchPattern =
    routePath && !routePath.endsWith("*")
      ? routePath.endsWith("/")
        ? `${routePath}*`
        : `${routePath}/*`
      : routePath || "";
  const routePathMatch = useMatch(routeMatchPattern);

  const isDropdownMenuButton =
    dropdownMenuContext !== null && !isDropdownMenuTrigger;
  const isActive = isActiveProp ?? !!routePathMatch;
  const onPress =
    onPressProp ??
    (() => {
      console.log(`Navigation: auto-navigating to ${routePath}`);
      navigate(routePath || "");
    });

  const leftIcon = icon
    ? getThemedIcon(icon, {
        size: "$1",
        color,
      })
    : undefined;

  const rightIcon = isLoading ? (
    <Spinner />
  ) : isDropdownMenuTrigger ? (
    getThemedIcon(dropdownMenuContext?.isOpen ? ChevronUp : ChevronDown, {
      size: "$1",
      color,
    })
  ) : (
    iconAfter
  );

  const renderContent = () => (
    <XStack width="100%" gap="$3.5" alignItems="center">
      {leftIcon}
      <Button.Text size="$4" flexGrow={1} textAlign="left">
        {children}
      </Button.Text>
      {rightIcon}
    </XStack>
  );

  if (!isDropdownMenuButton && isActive) {
    return (
      <GradientButton
        elevated
        size="$5"
        paddingHorizontal="$3.5"
        borderColor="$transparentBorder"
        borderRadius="$6"
        justifyContent="flex-start"
        disabled={isLoading}
        onPress={onPress}
        {...rest}
      >
        {renderContent()}
      </GradientButton>
    );
  }

  return (
    <Button
      size="$5"
      paddingHorizontal="$3.5"
      borderRadius={isDropdownMenuButton ? "$4" : "$6"}
      borderWidth={2}
      borderColor={isActive ? "$transparentBorder" : "transparent"}
      backgroundColor={isActive ? "$gray6" : "$gray4"}
      hoverStyle={{ backgroundColor: isActive ? "$gray7" : "$gray6" }}
      justifyContent="flex-start"
      disabled={isLoading}
      onPress={onPress}
      {...rest}
    >
      {renderContent()}
    </Button>
  );
};

type NavigationDropdownMenuContextProps = {
  isOpen: boolean;
  setIsOpen?: (open: boolean) => void;
};

const NavigationDropdownMenuContext =
  createContext<NavigationDropdownMenuContextProps | null>(null);

const NavigationDropdownMenu = ({
  trigger,
  children,
}: {
  trigger?: ReactElement;
  children?: ReactNode;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRoutePaths = getRoutePathsFromButtons(children);
  const isTriggerActive = dropdownRoutePaths.includes(pathname);

  const triggerProps = useMemo(
    () => ({
      isDropdownMenuTrigger: true,
      isActive: isTriggerActive,
      onPress: () => {
        setIsOpen((prev) => {
          if (!prev && dropdownRoutePaths.length > 0) {
            navigate(dropdownRoutePaths[0]);
          }
          return !prev;
        });
      },
    }),
    [isOpen, isTriggerActive],
  );

  const TriggerComponent = (props) =>
    trigger ? cloneElement(trigger, { ...props, ...triggerProps }) : null;

  useEffect(() => {
    if (dropdownRoutePaths.includes(pathname)) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [pathname]);

  return (
    <NavigationDropdownMenuContext.Provider value={{ isOpen, setIsOpen }}>
      <YStack gap="$3.5">
        <TriggerComponent />
        {isOpen && (
          <YStack padding="$2" borderRadius="$6" backgroundColor="$gray4">
            {children}
          </YStack>
        )}
      </YStack>
    </NavigationDropdownMenuContext.Provider>
  );
};

function getRoutePathsFromButtons(buttons: ReactNode): string[] {
  const routePaths: string[] = [];

  Children.forEach(buttons, (child) => {
    if (isValidElement(child)) {
      if (child.type === Fragment) {
        routePaths.push(...getRoutePathsFromButtons(child.props.children));
      } else if (child.props.routePath) {
        routePaths.push(child.props.routePath);
      }

      if (
        typeof child.type !== "string" &&
        (("displayName" in child.type &&
          child.type.displayName === "NavigationDropdownMenu") ||
          child.type.name === "NavigationDropdownMenu")
      ) {
        routePaths.push(...getRoutePathsFromButtons(child.props.children));
      }
    }
  });

  return routePaths;
}

export const Navigation = withStaticProperties(NavigationComponent, {
  Button: NavigationButton,
  DropdownMenu: NavigationDropdownMenu,
});
